import { indexWorkflow } from './scopes.ts';
import { getStepType } from './step-types.ts';
import type { FieldKind, FieldSpec } from './step-types.ts';
import type { Step, Workflow } from './types.ts';

export interface WorkflowDiff {
  added: string[];
  changed: string[];
  removed: string[];
}

export class EditError extends Error {}

function stable(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stable).join(',')}]`;
  if (typeof value === 'object' && value !== null) {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stable(v)}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

function fingerprints(workflow: Workflow | null): Map<string, string> {
  const out = new Map<string, string>();
  if (!workflow) return out;
  for (const location of indexWorkflow(workflow).all) {
    const { step } = location;
    if (out.has(step.id)) continue;
    const own: Record<string, unknown> = { ...step };
    delete own['steps'];
    // Where the step sits counts too, so two steps that swap places both show as changed.
    own['@at'] = [location.sectionId, location.scope.id, location.index];
    out.set(step.id, stable(own));
  }
  return out;
}

export function diffWorkflows(before: Workflow | null, after: Workflow): WorkflowDiff {
  const a = fingerprints(before);
  const b = fingerprints(after);
  return {
    added: [...b.keys()].filter((id) => !a.has(id)),
    changed: [...b.keys()].filter((id) => a.has(id) && a.get(id) !== b.get(id)),
    removed: [...a.keys()].filter((id) => !b.has(id)),
  };
}

export function findStep(workflow: Workflow, stepId: string): Step | null {
  return indexWorkflow(workflow).byId.get(stepId)?.step ?? null;
}

const TEXT_KINDS = new Set<FieldKind>([
  'text',
  'longtext',
  'doc',
  'duration',
  'variable',
  'connection',
  'url',
  'folder',
  'workflow',
  'choice',
]);
const KIND_WORDS: Partial<Record<FieldKind, string>> = {
  number: 'a number',
  options: 'a list',
  map: 'a set of named values',
};
for (const kind of TEXT_KINDS) KIND_WORDS[kind] = 'text';

/** Whether a value has the shape a field of this kind holds. */
function fitsKind(kind: FieldKind, value: unknown): boolean {
  if (TEXT_KINDS.has(kind)) return typeof value === 'string';
  if (kind === 'number') return typeof value === 'number' && Number.isFinite(value);
  if (kind === 'options') return Array.isArray(value);
  if (kind === 'recipients') return typeof value === 'string' || Array.isArray(value);
  return typeof value === 'object' && !Array.isArray(value);
}

export function updateStepFields(workflow: Workflow, stepId: string, patch: Record<string, unknown>): Workflow {
  const copy = structuredClone(workflow);
  const step = findStep(copy, stepId);
  if (!step) throw new EditError(`Step "${stepId}" does not exist.`);
  const spec = getStepType(step.type);
  for (const [key, value] of Object.entries(patch)) {
    const field = key === 'title' ? { editable: true, required: false } : spec?.fields.find((f) => f.name === key);
    if (!field || !field.editable) throw new EditError(`"${key}" cannot be edited here. Ask Ari to change it.`);
    if (value === undefined || value === null || value === '') {
      if (field.required) throw new EditError(`"${key}" is required.`);
      delete step[key];
    } else {
      const kind = key === 'title' ? 'text' : (field as FieldSpec).kind;
      if (!fitsKind(kind, value)) throw new EditError(`"${key}" needs ${KIND_WORDS[kind] ?? 'another kind of value'}.`);
      step[key] = value;
    }
  }
  return copy;
}
